import { useSyncExternalStore } from 'react';

/**
 * How far this tab is with sending an upload: files sent (landed or given up)
 * of the files granted. A zip is one file.
 */
export interface SendProgress {
  filesSent: number;
  filesTotal: number;
}

/**
 * Uploads whose bytes this tab is still sending, by upload id. The upload
 * dialog closes once an upload starts, so the Uploads page reads the
 * transfer progress from here instead.
 *
 * Module state on purpose: the transfer outlives the dialog, and the page
 * can remount while it runs.
 */
const sending = new Map<string, SendProgress>();
const listeners = new Set<() => void>();
let snapshot: ReadonlyMap<string, SendProgress> = new Map();

// Closing the tab mid-transfer drops the files that have not been sent.
const warnBeforeUnload = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  // Older browsers only show the prompt when returnValue is set.
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
};

function publish() {
  snapshot = new Map(sending);
  listeners.forEach((listener) => listener());
}

export const browserUploads = {
  start(uploadId: string, initial: SendProgress) {
    if (sending.size === 0) {
      window.addEventListener('beforeunload', warnBeforeUnload);
    }
    sending.set(uploadId, initial);
    publish();
  },
  progress(uploadId: string, next: SendProgress) {
    const current = sending.get(uploadId);
    if (!current || current.filesSent === next.filesSent) return;
    sending.set(uploadId, next);
    publish();
  },
  finish(uploadId: string) {
    if (!sending.delete(uploadId)) return;
    if (sending.size === 0) {
      window.removeEventListener('beforeunload', warnBeforeUnload);
    }
    publish();
  },
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** How far this tab is with sending an upload, or undefined when it is not sending it. */
export function useBrowserUploadProgress(
  uploadId: string
): SendProgress | undefined {
  const current = useSyncExternalStore(subscribe, () => snapshot);
  return current.get(uploadId);
}
